import API_ROUTES from "../constants/api";
import HttpUtil from "../http-handler/http-util";

export default class DashGraphConfigService {

  static getConfigs(
    pageNumber = 0,
    pageSize = 10,
    companyId = null,
    category = null,
    standardName = null,
    key = null
  ) {
    return HttpUtil.get(
      API_ROUTES.DASH_GRAPH_CONFIG.LIST
        .replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace(companyId ? '<companyId>' : '&companyId=<companyId>', companyId ?? '')
        .replace(category ? '<category>' : '&category=<category>', category ?? '')
        .replace(standardName ? '<standardName>' : '&standardName=<standardName>', standardName ? encodeURIComponent(standardName) : '')
        .replace(key ? '<key>' : '&key=<key>', key ?? '')
    );
  }

  static createConfig(payload) {
    return HttpUtil.post(API_ROUTES.DASH_GRAPH_CONFIG.CREATE, payload);
  }

  static updateConfig(payload) {
    return HttpUtil.put(API_ROUTES.DASH_GRAPH_CONFIG.UPDATE, payload);
  }

  static getProps(type) {
    return HttpUtil.get(
      API_ROUTES.DASH_GRAPH_CONFIG.GET_PROPS
        .replace(type ? "<type>" : "?type=<type>", type ?? '')
    );
  }

  static getConfig(companyId, cat = null, standardName = null, topicName = null, emission = false, forSuperAdmin = false) {
    if (forSuperAdmin) {
      return HttpUtil.get(
        API_ROUTES.DASH_GRAPH_CONFIG.GET
          .replace("<companyId>", companyId)
          .replace(cat ? '<category>' : '&category=<category>', cat ?? '')
          .replace(standardName ? '<standardName>' : '&standardName=<standardName>', standardName ? encodeURIComponent(standardName) : '')
          .replace(topicName ? '<topicName>' : '&topicName=<topicName>', topicName ?? '')
      );
    }
    else {
      return HttpUtil.get(
        API_ROUTES.DASH_GRAPH_CONFIG.GET_COMPANY_GRAPHS
          .replace("<companyId>", companyId)
          .replace("<standard>", 0)
          .replace(cat ? '<category>' : '&category=<category>', cat ?? '')
          .replace(standardName ? '<standardName>' : '&standardName=<standardName>', standardName ? encodeURIComponent(standardName) : '')
          .replace(topicName ? '<topicName>' : '&topicName=<topicName>', topicName ?? '')
          .replace(emission ? '<emission>' : '&emission=<emission>', emission ? emission : '')
          .replace('<active>', true)
      );
    }

  }

  static getAllConfigs(cat = null) {
    return HttpUtil.get(API_ROUTES.DASH_GRAPH_CONFIG.LIST_UNPAGED
      .replace(cat ? '<category>' : '?category=<category>', cat ?? ''));
  }

  static assignToCompany(payload, companyId) {
    return HttpUtil.patch(API_ROUTES.DASH_GRAPH_CONFIG.ASSIGN_TO_COMPANY
      .replace('<companyId>', companyId), payload);
  }

  static deleteConfig(id) {
    return HttpUtil.delete(API_ROUTES.DASH_GRAPH_CONFIG.DELETE
      .replace('<id>', id)
    );
  }

  static getGraph(graphId, period, standardId, businessUnitId = null, interval = null, offset = null) {
    return HttpUtil.get(API_ROUTES.DASH_GRAPH_CONFIG.GET_GRAPH
      .replace('<graphId>', graphId)
      .replace('<period>', period)
      .replace('<standardId>', standardId)
      .replace('<businessUnitId>', businessUnitId ?? '')
      .replace('<interval>', interval ?? '')
      .replace('<offset>', offset ?? '')
    );
  }

  static getGraphWithMetrics(standardId, periodFrom = null, periodTo = null, businessUnitIds = null, metricIds = null, metricDataPointIds = null) {
    // Set default dates if not provided (Jan 1 to Dec 31 of current year)
    const currentYear = new Date().getFullYear();
    const defaultPeriodFrom = `${currentYear}-01-01`;
    const defaultPeriodTo = `${currentYear}-12-31`;
    
    return HttpUtil.get(API_ROUTES.DASH_GRAPH_CONFIG.GET_GRAPH_WITH_METRICS
      .replace('<standardId>', standardId)
      .replace('<metricIds>', metricIds || '')
      .replace('<metricDataPointIds>', metricDataPointIds || '')
      .replace('<periodFrom>', periodFrom || defaultPeriodFrom)
      .replace('<periodTo>', periodTo || defaultPeriodTo)
      .replace('<businessUnitIds>', businessUnitIds || '')
    );
  }

  static getDataView(dataObject) {
    return HttpUtil.get(API_ROUTES.DASH_GRAPH_CONFIG.GET_DATA_VIEW
      .replace('<standardId>', dataObject.standardId)
      .replace('<graphId>', dataObject.graphId)
      .replace('<period>', dataObject.period)
      .replace('<categoryType>', dataObject.categoryType)
      .replace('<businessUnitIds>', dataObject.businessUnitIds)
      .replace('<periodFrom>', dataObject.periodFrom)
      .replace('<periodTo>', dataObject.periodTo)
      .replace('<interval>', dataObject.interval)
      .replace('<offset>', dataObject.offset)
      .replace('<aggregationType>', dataObject.aggregationType)
      .replace('<includeTrends>', dataObject.includeTrends)
    );
  }

  static changeStatus(graphId, companyId) {
    return HttpUtil.patch(API_ROUTES.DASH_GRAPH_CONFIG.CHANGE_STATUS
      .replace('<id>', graphId)
      .replace('<companyId>', companyId)
    );
  }

  static getCompanyGraphs(companyId, standardId, cat = null, standardName = null, 
    topicName = null, emission = false, active = false, fetchAll = false, 
    scope ='', scopeCategory = '', layout = -1) {
    return HttpUtil.get(
      API_ROUTES.DASH_GRAPH_CONFIG.GET_COMPANY_GRAPHS
        .replace("<companyId>", companyId)
        .replace("<standard>", standardId)
        .replace(cat ? '<category>' : '&category=<category>', cat ?? '')
        .replace(standardName ? '<standardName>' : '&standardName=<standardName>', standardName ? encodeURIComponent(standardName) : '')
        .replace(topicName ? '<topicName>' : '&topicName=<topicName>', topicName ?? '')
        .replace(emission ? '<emission>' : '&emission=<emission>', emission ? emission : '')
        .replace('<active>', active)
        .replace('<fetchAll>', fetchAll)
        .replace(scope ? '<scope>' : '&scope=<scope>', scope ?? '')
        .replace(scopeCategory ? '<scopeCategory>' : '&scopeCategory=<scopeCategory>', scopeCategory ?? '')
        .replace(layout >= 0 ? '<layout>' : '&layout=<layout>', layout >= 0 ? layout : '')
    );
  }

  static activateGraph(id, status) {
    return HttpUtil.patch(
      API_ROUTES.DASH_GRAPH_CONFIG.ACTIVATE_GRAPH
        .replace("<id>", id)
        .replace("<status>", status), {}
    );
  }

  static saveGraphLayout(standardId, layout) {
    return HttpUtil.post(
      API_ROUTES.DASH_GRAPH_CONFIG.SAVE_GRAPH_LAYOUT
        .replace("<standardId>", standardId), layout
    );
  }

  static createGraphSet(payload) {
    return HttpUtil.post(API_ROUTES.DASH_GRAPH_CONFIG.CREATE_GRAPH_SET, payload);
  }

  static getGraphSets(pageNumber = 0, pageSize = 10, key = null) {
    return HttpUtil.get(
      API_ROUTES.DASH_GRAPH_CONFIG.LIST_GRAPH_SETS
        .replace("<pageNumber>", pageNumber)
        .replace("<pageSize>", pageSize)
        .replace("<keyQuery>", key ? `&key=${encodeURIComponent(key)}` : "")
    );
  }

  static assignSetToCompany(setId, companyId, standardId) {
    return HttpUtil.post(
      API_ROUTES.DASH_GRAPH_CONFIG.ASSIGN_SET_TO_COMPANY.replace("<id>", setId),
      { companyId, standardId }
    );
  }

  static deleteGraphSet(setId) {
    return HttpUtil.delete(API_ROUTES.DASH_GRAPH_CONFIG.DELETE_GRAPH_SET.replace("<id>", setId));
  }

  static unassignSetFromCompany(setId, companyId) {
    return HttpUtil.delete(
      API_ROUTES.DASH_GRAPH_CONFIG.UNASSIGN_SET_FROM_COMPANY
        .replace("<id>", setId)
        .replace("<companyId>", companyId)
    );
  }

  static updateGraphSet(setId, payload) {
    return HttpUtil.patch(
      API_ROUTES.DASH_GRAPH_CONFIG.UPDATE_GRAPH_SET.replace("<id>", setId),
      payload
    );
  }

  static updateCompanyGraph(payload) {
    return HttpUtil.put(API_ROUTES.DASH_GRAPH_CONFIG.UPDATE_COMPANY_GRAPH, payload);
  }
}
